import { AlertCircle, Bell, CheckCircle, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import api from "../api/api";
import NotificationService from "../services/NotificationService";
import { logger } from "../utils/logger";
const NotificationOptIn = ({ userId }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [supportDetails, setSupportDetails] = useState(null);

  useEffect(() => {
    // Simulating notification support check
    setSupportDetails({ isSupported: true });
  }, []);

  const handleEnable = async () => {
    setIsLoading(true);
    setStatus(null);
    try {
      // Get browser timezone
      const browserTimezone = (() => {
        try {
          return window.Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (error) {
          return "UTC";
        }
      })();

      await NotificationService.subscribe();
      
      // Update user preferences with browser timezone and enable notifications
      await api.patch(`/users/${userId}/`, {
        receives_notifications: true,
        notification_timezone: browserTimezone,
      });
      
      setStatus("success");
      setTimeout(() => setIsVisible(false), 2000);
    } catch (error) {
      logger.error("Error enabling notifications:", error);
      setStatus("error");
    }
    setIsLoading(false);
  };

  const handleDismissal = async () => {
    setIsVisible(false);
    try {
      await api.patch(`/users/${userId}/`, { receives_notifications: false });
    } catch (error) {
      console.error("Error updating notification preferences:", error);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="container-fluid px-3 px-md-4 mb-4">
        <div className="border-start border-4 border-pink bg-white shadow-sm rounded-3 p-3 p-md-4 position-relative">
          <button
            onClick={() => setIsVisible(false)}
            className="btn-close position-absolute top-0 end-0 mt-3 me-3"
            aria-label="Close"
          />

          <div className="d-flex flex-column flex-md-row align-items-start gap-3">
            <div className="rounded-circle bg-pink-light p-3 flex-shrink-0">
              <Bell size={24} className="text-pink" />
            </div>

            <div className="flex-grow-1">
              <h5 className="mb-2 fw-bold">Enable Notifications?</h5>
              <p className="mb-3 text-secondary">
                Notifications can help you stay on track with your programs.
                Customize what time you receive notifications in your Profile.
              </p>

              {!supportDetails?.isSupported && (
                <div className="alert alert-warning d-flex align-items-center gap-2 mb-3">
                  <AlertCircle size={18} />
                  <span>
                    Notifications are not supported on your device/browser.
                  </span>
                </div>
              )}

              {status === "success" && (
                <div className="alert alert-success d-flex align-items-center gap-2 mb-3">
                  <CheckCircle size={18} />
                  <span>Notifications enabled successfully!</span>
                </div>
              )}

              {status === "error" && (
                <div className="alert alert-danger d-flex align-items-center gap-2 mb-3">
                  <XCircle size={18} />
                  <span>Failed to enable notifications. Please ensure notifications are enabled in your device/browser settings and try again.</span>
                </div>
              )}

              <div className="d-flex flex-wrap gap-2">
                {supportDetails?.isSupported ? (
                  <button
                    className="btn btn-pink d-inline-flex align-items-center gap-2"
                    onClick={handleEnable}
                    disabled={isLoading || status === "success"}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        <span>Enabling...</span>
                      </>
                    ) : status === "success" ? (
                      <>
                        <CheckCircle size={18} />
                        <span>Enabled!</span>
                      </>
                    ) : (
                      <>
                        <Bell size={18} />
                        <span>Enable Notifications</span>
                      </>
                    )}
                  </button>
                ) : (
                  <a
                    href="https://caniuse.com/push-api"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-pink d-inline-flex align-items-center gap-2"
                  >
                    <Bell size={18} />
                    <span>Learn More</span>
                  </a>
                )}

                {status !== "success" && (
                  <button
                    className="btn btn-link text-secondary text-decoration-none"
                    onClick={handleDismissal}
                  >
                    Maybe Later
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
NotificationOptIn.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default NotificationOptIn;
